import { useState, useEffect } from 'react';
import type { FC, FormEvent } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { adminFetch } from '../utils/api';
import { formatCents } from '../utils/format';
import Modal from '../components/Modal';

interface Affiliate {
  code: string;
  name: string;
  email: string;
  activationBalanceCents: number;
  commissionPercent: number;
  referredUserCount: number;
  referredDepositCents: number;
  referredSpendCents: number;
  createdAt: string;
  updatedAt: string;
}

const Affiliates: FC = () => {
  const { token } = useAuth();
  const [affiliates, setAffiliates] = useState<Affiliate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<Affiliate | null>(null);

  const loadAffiliates = async () => {
    if (!token) return;
    try {
      const data = await adminFetch<{ affiliates: Affiliate[] }>('/admin/affiliates', token);
      setAffiliates(data.affiliates);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load affiliates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAffiliates(); }, [token]);

  if (loading) return <div className="text-center mt-5"><div className="spinner-border" /></div>;

  return (
    <div className="p-3">
      <div className="d-flex justify-content-end mb-3">
        <button className="btn btn-primary btn-sm" onClick={() => setShowCreate(true)}>
          Create Affiliate
        </button>
      </div>

      {error && <div className="alert alert-warning py-2 small">{error}</div>}

      <table className="table table-striped table-hover table-sm align-middle mb-0">
        <thead className="table-dark">
          <tr>
            <th>Code</th>
            <th>Name</th>
            <th>Email</th>
            <th className="text-end">Activation</th>
            <th className="text-end">Commission</th>
            <th className="text-end">Users</th>
            <th className="text-end">Deposits</th>
            <th className="text-end">Spend</th>
          </tr>
        </thead>
        <tbody>
          {affiliates.map(aff => (
            <tr
              key={aff.code}
              onClick={() => setEditing(aff)}
              style={{ cursor: 'pointer' }}
            >
              <td><code className="small">{aff.code}</code></td>
              <td>{aff.name}</td>
              <td className="small">{aff.email}</td>
              <td className="text-end">{formatCents(aff.activationBalanceCents)}</td>
              <td className="text-end">{aff.commissionPercent}%</td>
              <td className="text-end">{aff.referredUserCount ?? 0}</td>
              <td className="text-end">{formatCents(aff.referredDepositCents ?? 0)}</td>
              <td className="text-end">{formatCents(aff.referredSpendCents ?? 0)}</td>
            </tr>
          ))}
          {affiliates.length === 0 && (
            <tr><td colSpan={8} className="text-center text-muted">No affiliates yet</td></tr>
          )}
        </tbody>
      </table>

      {showCreate && (
        <CreateAffiliateDialog
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); loadAffiliates(); }}
        />
      )}

      {editing && (
        <EditAffiliateDialog
          affiliate={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); loadAffiliates(); }}
        />
      )}
    </div>
  );
};

const CreateAffiliateDialog: FC<{ onClose: () => void; onCreated: () => void }> = ({ onClose, onCreated }) => {
  const { token } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [balance, setBalance] = useState('');
  const [commission, setCommission] = useState('10');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setSubmitting(true);
    setError('');
    try {
      await adminFetch('/admin/affiliates', token, {
        method: 'POST',
        body: {
          name,
          email,
          activationBalanceCents: Math.round(parseFloat(balance) * 100),
          commissionPercent: parseFloat(commission),
        },
      });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create affiliate');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal title="Create Affiliate" onClose={onClose} closeDisabled={submitting}>
      <form onSubmit={handleSubmit}>
        <div className="modal-body">
          {error && <div className="alert alert-danger py-2 small">{error}</div>}
          <div className="mb-2">
            <label className="form-label small">Name</label>
            <input type="text" className="form-control form-control-sm" value={name} onChange={e => setName(e.target.value)} required disabled={submitting} />
          </div>
          <div className="mb-2">
            <label className="form-label small">Email</label>
            <input type="email" className="form-control form-control-sm" value={email} onChange={e => setEmail(e.target.value)} required disabled={submitting} />
          </div>
          <div className="mb-2">
            <label className="form-label small">Activation Balance (USD)</label>
            <input type="number" step="0.01" className="form-control form-control-sm" value={balance} onChange={e => setBalance(e.target.value)} placeholder="e.g. 1.00" required disabled={submitting} />
          </div>
          <div className="mb-2">
            <label className="form-label small">Commission %</label>
            <input type="number" step="0.1" className="form-control form-control-sm" value={commission} onChange={e => setCommission(e.target.value)} required disabled={submitting} />
          </div>
        </div>
        <div className="modal-footer py-2">
          <button type="button" className="btn btn-sm btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
          <button type="submit" className="btn btn-sm btn-primary" disabled={submitting}>
            {submitting ? 'Creating...' : 'Create'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

const EditAffiliateDialog: FC<{ affiliate: Affiliate; onClose: () => void; onSaved: () => void }> = ({ affiliate, onClose, onSaved }) => {
  const { token } = useAuth();
  const [name, setName] = useState(affiliate.name);
  const [email, setEmail] = useState(affiliate.email);
  const [balance, setBalance] = useState((affiliate.activationBalanceCents / 100).toFixed(2));
  const [commission, setCommission] = useState(String(affiliate.commissionPercent));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setSubmitting(true);
    setError('');
    try {
      await adminFetch(`/admin/affiliates/${affiliate.code}`, token, {
        method: 'PATCH',
        body: {
          name,
          email,
          activationBalanceCents: Math.round(parseFloat(balance) * 100),
          commissionPercent: parseFloat(commission),
        },
      });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update affiliate');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal title={`Edit Affiliate ${affiliate.code}`} onClose={onClose} closeDisabled={submitting}>
      <form onSubmit={handleSubmit}>
        <div className="modal-body">
          {error && <div className="alert alert-danger py-2 small">{error}</div>}
          <div className="mb-2">
            <label className="form-label small">Code</label>
            <input type="text" className="form-control form-control-sm" value={affiliate.code} disabled />
          </div>
          <div className="mb-2">
            <label className="form-label small">Name</label>
            <input type="text" className="form-control form-control-sm" value={name} onChange={e => setName(e.target.value)} required disabled={submitting} />
          </div>
          <div className="mb-2">
            <label className="form-label small">Email</label>
            <input type="email" className="form-control form-control-sm" value={email} onChange={e => setEmail(e.target.value)} required disabled={submitting} />
          </div>
          <div className="mb-2">
            <label className="form-label small">Activation Balance (USD)</label>
            <input type="number" step="0.01" className="form-control form-control-sm" value={balance} onChange={e => setBalance(e.target.value)} required disabled={submitting} />
          </div>
          <div className="mb-2">
            <label className="form-label small">Commission %</label>
            <input type="number" step="0.1" className="form-control form-control-sm" value={commission} onChange={e => setCommission(e.target.value)} required disabled={submitting} />
          </div>
          <div className="row small text-muted mt-3">
            <div className="col-6">Users: {affiliate.referredUserCount ?? 0}</div>
            <div className="col-6">Deposits: {formatCents(affiliate.referredDepositCents ?? 0)}</div>
          </div>
        </div>
        <div className="modal-footer py-2">
          <button type="button" className="btn btn-sm btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
          <button type="submit" className="btn btn-sm btn-primary" disabled={submitting}>
            {submitting ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default Affiliates;
